// Typed client for the claim-first RPCs added by
// tour-builder-public/supabase/migrations/20260905150000_provider_claim_first.sql
// and 20260905160000_provider_claim_search.sql.
//
// Every one of these is SECURITY DEFINER and takes its identity from auth.uid()
// inside the database — never from anything this file sends. The console cannot
// grant itself a claim by lying about who it is; the worst a tampered client can
// do is ask for a claim it will be refused.
//
// The claim RPC returns a result object rather than throwing, because the
// database ledgers refused attempts too and a RAISE would roll its own ledger
// row back. So `ok: false` here is a normal, expected answer with a message
// already written for the provider — render it, don't treat it as an error.

import { supabase } from './supabase';

export interface ClaimableProfile {
  id: string;
  business_name: string;
  address: string | null;
  business_type: string | null;
  already_claimed: boolean;
}

export type ClaimReason =
  | 'claimed'
  | 'already_yours'
  | 'already_claimed'
  | 'no_such_profile'
  | 'no_proof'
  | 'inactive'
  | 'owns_other_profile'
  | 'not_signed_in';

export interface ClaimResult {
  ok: boolean;
  reason: ClaimReason;
  message?: string;
  profile_id?: string;
  business_name?: string;
  method?: 'email' | 'code';
}

export interface ResolvedCode {
  ok: boolean;
  reason?: 'invalid_code' | 'code_used' | 'code_expired' | 'already_claimed' | 'not_signed_in';
  profile_id?: string;
  business_name?: string;
  address?: string | null;
}

/** Normalise a typed or scanned code: strip separators, upper-case. */
export function normaliseCode(raw: string): string {
  return raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

export async function searchClaimable(query: string): Promise<ClaimableProfile[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  const { data, error } = await supabase.rpc('search_claimable_profiles', { p_query: q });
  if (error) throw new Error(error.message);
  return (data ?? []) as ClaimableProfile[];
}

export async function resolveClaimCode(code: string): Promise<ResolvedCode> {
  const { data, error } = await supabase.rpc('resolve_provider_claim_code', {
    p_code: normaliseCode(code),
  });
  if (error) throw new Error(error.message);
  return (data ?? { ok: false, reason: 'invalid_code' }) as ResolvedCode;
}

export async function claimProfile(profileId: string, code?: string): Promise<ClaimResult> {
  const trimmed = code ? normaliseCode(code) : '';
  const { data, error } = await supabase.rpc('claim_provider_profile', {
    p_profile_id: profileId,
    p_code: trimmed.length > 0 ? trimmed : null,
  });
  if (error) throw new Error(error.message);
  return data as ClaimResult;
}
